import React, { useRef, useState } from 'react';

// TODO(VRG-173): promote to categorized app component library / @repo/ui once
// apps/app gets its own component library (VERA-837 build-decision note).

import { Button as UiButton } from '@repo/ui';
import { Box, Stack, Typography } from '@mui/material';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@repo/shadcn-ui';

import { AuthErrorMessage, CodeExpiryNotice, TwoFactorChallenge } from 'components/auth';
import { OTP_CODE_TTL_SECONDS, useCodeExpiry } from 'hooks/useCodeExpiry';
import type { AuthFactorType } from '@repo/types';
import { useSendMagicAuthCode, useVerifyMagicAuthCode } from 'hooks/api/useWorkOSAuth';
import { otpSendErrorMessage, otpVerifyErrorMessage } from 'utils';

// `isRawHttpStatus` lived here and was duplicated by prose in two other files. It now
// lives inside `otpVerifyErrorMessage` / `otpSendErrorMessage` (utils/otpError), with the
// three call sites reduced to one line each.

interface OnboardingAuthPanelProps {
  /** Prefilled from the invite link's `email` query param. */
  email: string;
  /** Prefilled from the invite link's `companyId` query param, when present. */
  companyId?: string;
  /**
   * Typed name captured on the self-serve create-account form (VERA-837).
   * Optional — the invite-link flows (Claim-Ownership) don't collect a name
   * up front, so this panel remains usable without it. Threaded through to
   * `verifyMagicAuthCode` so a NEW owner is provisioned with a real name
   * even though WorkOS OTP profiles carry none.
   */
  firstName?: string;
  lastName?: string;
  /** VRG-357 — handle chosen at signup; provisioned onto a NEW owner. */
  username?: string;
  /**
   * When true, the panel renders straight into the code-entry step instead
   * of its own "Send Code" screen — used by the self-serve create-account
   * form (VERA-837), which sends the OTP itself as part of its "Create
   * account" submit before mounting this panel.
   */
  initialCodeSent?: boolean;
  /**
   * VERA-837 — Terms & Conditions acceptance from the self-serve
   * create-account form's checkbox. Threaded through to
   * `verifyMagicAuthCode` so the API records the acceptance once the
   * account exists. Both must be present together — `termsVersion` alone
   * or `acceptedTerms` alone is never sent. Omitted by flows that don't
   * collect a checkbox (Claim-Ownership invite link).
   */
  acceptedTerms?: boolean;
  termsVersion?: string;
  /**
   * Called once magic-auth verification succeeds.
   *
   * VRG-500 — takes a RESULT OBJECT rather than a bare `isNewUser` boolean. The
   * old single argument was read by none of this component's three callers, and
   * the one page that wanted it (`/claim`) wanted the other question: `isNewUser`
   * means "first time at this company" and is FALSE for a brand-new gift
   * recipient, whose `users` row and `company_users` link are both provisioned by
   * the send. Naming both fields makes it impossible to reach for the wrong one
   * by accident, which a second positional boolean would not have.
   */
  onVerified: (result: {
    /** First time signing in AT THIS COMPANY. */
    isNewUser: boolean;
    /** Whether the address already had a Vera account before this verification. */
    hasAccount: boolean;
  }) => void;
}

/**
 * Shared "verify your email to continue" panel for the onboarding email
 * flows (VERA-837 self-serve create-account, VERA-843 Account Email Path
 * invite link, VERA-839 Claim-Ownership Sign-Up). Built on the same WorkOS
 * magic-auth hooks + `AuthErrorMessage` as `SignInBanner`
 * (apps/app/src/pages/SignIn/SignInBanner.tsx), trimmed down to just the
 * send-code -> verify-code steps (no password fallback — these are one-shot
 * flows, not the general sign-in surface).
 */
export const OnboardingAuthPanel = ({
  email,
  companyId,
  firstName,
  lastName,
  username,
  initialCodeSent = false,
  acceptedTerms,
  termsVersion,
  onVerified,
}: OnboardingAuthPanelProps) => {
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(initialCodeSent);

  /*
   * VRG-720 — when the outstanding code was sent, so its 10-minute deadline can
   * be shown and its expiry told apart from a mistyped code.
   *
   * Seeded with `Date.now()` under `initialCodeSent` because every caller that
   * passes that flag (`CreateAccountEmail`, `TagNotActive`, `ClaimOnScanCard`)
   * sends the code immediately before mounting this panel — so mount time is
   * the send time to within one round-trip, not a guess.
   */
  const [codeIssuedAt, setCodeIssuedAt] = useState<number | null>(
    initialCodeSent ? Date.now() : null,
  );
  const { expired: codeExpired } = useCodeExpiry(codeIssuedAt, OTP_CODE_TTL_SECONDS);
  const [error, setError] = useState<React.ReactNode>(null);

  const [isSendingCode, setIsSendingCode] = useState(false);
  const sendCodeInFlightRef = useRef(false);

  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const verifyInFlightRef = useRef(false);

  /*
   * VRG-720 — a verified EMAIL code is not always a session.
   *
   * An account with a second factor answers `magic-auth/verify` with
   * `{success: true, challengeRequired: true, ticket, factorType}` and NO
   * `access_token`: the primary factor genuinely checked out, and the session is
   * withheld pending the second. `useVerifyMagicAuthCode`'s own `onSuccess`
   * already guards on that (`if (!data.access_token) return`), and
   * `workos-auth.service` says in as many words that callers must branch on the
   * discriminant before storing — but this panel did not, so it reported
   * "verified" to its caller with nothing in storage. In the claim flow that
   * meant the claim fired token-less and came back 401, surfacing as "We could
   * not confirm your sign in" on a code that was in fact correct, with no way
   * forward. `SignInBanner` and the certificate sign-in drawer have carried this
   * branch since VRG-490; this panel was simply left off the list.
   */
  const [challenge, setChallenge] = useState<{
    ticket: string;
    factorType: AuthFactorType;
    hasAccount: boolean;
  } | null>(null);

  const sendMagicAuthCode = useSendMagicAuthCode();
  const verifyMagicAuthCode = useVerifyMagicAuthCode();

  const handleSendCode = async () => {
    if (sendCodeInFlightRef.current || !email) return;

    sendCodeInFlightRef.current = true;
    setIsSendingCode(true);
    setError(null);
    try {
      await sendMagicAuthCode.mutateAsync({ email, companyId });
      setCodeSent(true);
      // A resend OVERWRITES the stored code and restarts its TTL ("newest wins
      // on resend", LoginOtpService.send), so the clock restarts here too.
      setCodeIssuedAt(Date.now());
    } catch (err: any) {
      setError(otpSendErrorMessage(err));
    } finally {
      sendCodeInFlightRef.current = false;
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verifyInFlightRef.current) return;

    if (!code || code.length !== 6) {
      setError('Please enter a valid 6-digit code.');
      return;
    }

    verifyInFlightRef.current = true;
    setIsVerifyingCode(true);
    setError(null);
    try {
      const result = await verifyMagicAuthCode.mutateAsync({
        email,
        code,
        username,
        companyId,
        firstName,
        lastName,
        acceptedTerms,
        termsVersion,
      });

      // The session is withheld until the second factor is answered — see the
      // `challenge` docblock. Hand over to the challenge screen rather than
      // telling the caller it is signed in.
      if (result.challengeRequired && result.ticket && result.factorType) {
        setChallenge({
          ticket: result.ticket,
          factorType: result.factorType,
          hasAccount: result.hasAccount ?? true,
        });
        return;
      }

      onVerified({
        isNewUser: result.isNewUser ?? false,
        // VRG-500: defaults TRUE, i.e. "assume returning", on a response that
        // omits the field. An older API build is the realistic cause, and the
        // failure this default picks is the quieter one — a first-timer shown a
        // sign-in reads as ordinary, whereas greeting someone who has had an
        // account for a year with "you've been invited to join" reads as a bug.
        hasAccount: result.hasAccount ?? true,
      });
    } catch (err: any) {
      // See `otpVerifyErrorMessage` — a failure AFTER the code verified is not a bad code.
      setError(otpVerifyErrorMessage(err));
      setCode('');
    } finally {
      verifyInFlightRef.current = false;
      setIsVerifyingCode(false);
    }
  };

  if (challenge) {
    return (
      <TwoFactorChallenge
        ticket={challenge.ticket}
        factorType={challenge.factorType}
        onBack={() => {
          // The ticket is abandoned, not cancelled — it burns itself on its own
          // 5-minute TTL. Back lands on code entry with the field cleared, so a
          // fresh code can be requested without leaving the claim.
          setChallenge(null);
          setCode('');
          setError(null);
        }}
        onVerified={(result) => {
          setChallenge(null);
          onVerified({
            isNewUser: result.isNewUser ?? false,
            hasAccount: challenge.hasAccount,
          });
        }}
      />
    );
  }

  if (!codeSent) {
    return (
      <Box>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          We'll send a 6-digit verification code to <strong>{email}</strong>.
        </Typography>
        <AuthErrorMessage message={error} />
        <UiButton
          variant='primary'
          fullWidth
          onClick={handleSendCode}
          // VERA-837: never disabled for missing input — `handleSendCode`
          // no-ops without an email (see its guard above); the in-flight
          // state is the only legitimate disable reason here.
          disabled={isSendingCode}
        >
          {isSendingCode ? 'Sending…' : 'Send Code'}
        </UiButton>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant='body2' sx={{ mb: 4 }}>
        Enter the 6-digit code sent to {email}
      </Typography>
      <div className='mb-6'>
        {/*
          VERA-837: Enter submits. Shadow DOM means <form onSubmit> never
          fires, so Enter is wired on the OTP container (the slots are
          individually focused, so a per-slot handler would miss).
        */}
        <div
          className='flex flex-col'
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleVerifyCode();
            }
          }}
        >
          <Typography className='mb-2'>Verification Code</Typography>
          <InputOTP
            onChange={(value: string) => {
              const cleanedValue = value.replace(/\D/g, '').slice(0, 6);
              setCode(cleanedValue);
            }}
            value={code}
            maxLength={6}
            id='onboarding-otp'
            required
          >
            <InputOTPGroup className='flex w-full gap-2 *:data-[slot=input-otp-slot]:rounded-md *:data-[slot=input-otp-slot]:border'>
              <InputOTPSlot className='rounded-md border flex-1 h-12' index={0} />
              <InputOTPSlot className='rounded-md border flex-1 h-12' index={1} />
              <InputOTPSlot className='rounded-md border flex-1 h-12' index={2} />
              <InputOTPSlot className='rounded-md border flex-1 h-12' index={3} />
              <InputOTPSlot className='rounded-md border flex-1 h-12' index={4} />
              <InputOTPSlot className='rounded-md border flex-1 h-12' index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>
      </div>
      <CodeExpiryNotice
        issuedAt={codeIssuedAt}
        ttlSeconds={OTP_CODE_TTL_SECONDS}
        resendLabel='Use Resend Code below'
      />
      <AuthErrorMessage message={error} />
      <Stack spacing={2}>
        <UiButton
          /*
           * VRG-720: once the code has expired, Resend becomes the primary and
           * Verify steps back — but Verify stays ENABLED, deliberately.
           *
           * The countdown is a PREDICTION of the server's answer, not the
           * answer: it is driven by this device's clock, and only the API knows
           * whether the code is still in Redis. Disabling on a prediction can
           * lock someone out of a code the server would have taken. (Contrast
           * `TwoFactorChallenge`, which DOES disable on `expired` — there the
           * flag is set from an actual 404, i.e. the server's own verdict. The
           * two are consistent once you see which one is evidence.)
           *
           * It also keeps VERA-837's stance below: a button that is dead for a
           * reason the user cannot read is worse than one that explains itself
           * on click. Here the reason IS readable — the expiry notice sits
           * directly above — which is what earns the demotion.
           */
          variant={codeExpired ? 'secondary' : 'primary'}
          fullWidth
          onClick={handleVerifyCode}
          // VERA-837: never disabled because the code is incomplete —
          // clicking surfaces "Please enter a valid 6-digit code." via
          // `handleVerifyCode`'s own guard instead of a dead button. The
          // in-flight state is the only legitimate disable reason here;
          // double-submit is separately guarded by `verifyInFlightRef`.
          disabled={isVerifyingCode}
        >
          {isVerifyingCode ? 'Verifying…' : 'Verify Code'}
        </UiButton>
        <UiButton
          variant={codeExpired ? 'primary' : 'secondary'}
          fullWidth
          // VERA-837: actually RESEND. This previously did setCodeSent(false),
          // which just navigated back to the pre-send confirmation screen and
          // made the user press "Send Code" again — a go-back button labelled
          // resend. Now it re-sends in place and stays on the code step.
          onClick={async () => {
            setCode('');
            setError(null);
            await handleSendCode();
          }}
          disabled={isVerifyingCode || isSendingCode}
        >
          {isSendingCode ? 'Sending…' : 'Resend Code'}
        </UiButton>
      </Stack>
    </Box>
  );
};
